import { useTranslations } from 'next-intl';
import { Suspense } from 'react';
import { GameCategoryRender } from '@/components/entities/games/GameCategoryRender/GameCategoryRender';
import { useCampaignCategory, withoutCampaignCategory } from '@/components/features/campaigns';
import EmptyStateVisual from '@/components/shared/EmptyStateVisual';
import { Button } from '@/components/ui/Button/button';
import { useGroupCategories } from '@/hooks/useGroupCategories';
import { AppRoutes } from '@/i18n/constants';
import { Link } from '@/i18n/routing';
import { isStaging } from '@/libs/getEnvironment';

type EmptyGamesStateProps = {
  isSearch: boolean;
  isFavorites?: boolean;
};

export default function EmptyGamesState({ isSearch, isFavorites = false }: EmptyGamesStateProps) {
  const t = useTranslations('game.favorites-games');
  const campaignCategory = useCampaignCategory();
  const groups = useGroupCategories();
  const beforeFavoritesCategories = withoutCampaignCategory(groups.beforeFavoritesCategories, campaignCategory);
  const afterFavoritesCategories = withoutCampaignCategory(groups.afterFavoritesCategories, campaignCategory);
  const categoryIdByEnvironment = isStaging() ? '7f49c58a-5f65-4d25-84e3-aef4c67102f9' : '8614a166-259a-42a5-b028-38c979226615';

  return (
    <div className="w-full">
      <div className="flex flex-col items-center justify-center text-center relative mt-4.75 lg:mt-2.5">
        <EmptyStateVisual slot={isFavorites ? 'emptyFavorites' : 'emptyState'} />

        <div className="relative flex w-full flex-col items-center">
          <h2 className="text-white font-medium text-[1.25rem] leading-[130%] bottom-15 lg:bottom-20 mb-2.75">
            {isSearch
              ? t.rich('empty-state-search-title', {
                  br: () => <br />,
                })
              : t('empty-state-title')}
          </h2>

          {!isSearch
            ? (
                <p className="font-regular text-sm leading-5 text-c300">{t('empty-state-title-description')}</p>
              )
            : null}

          <Button asChild className="py-3 max-lg:w-full max-w-90 mx-auto lg:px-[1.575rem] lg:py-4 mt-6.25">
            <Link href={AppRoutes.categoryIdPaged(categoryIdByEnvironment)}>
              {t('empty-state-description')}
            </Link>
          </Button>
        </div>
      </div>

      <div className="relative w-full mt-10.25">
        {campaignCategory && (
          // As in the lobby, the Active Campaign's row comes first, in its own boundary.
          <Suspense fallback={null}>
            <GameCategoryRender
              categoryName={campaignCategory.category.name}
              title={campaignCategory.lobbyTitle}
              categoryId={campaignCategory.category.id}
            />
          </Suspense>
        )}
        {beforeFavoritesCategories.map(category => (
          <GameCategoryRender
            key={category.id}
            categoryName={category.name}
            categoryId={category.id}
          />
        ))}
        {afterFavoritesCategories.map(category => (
          <GameCategoryRender key={category.id} categoryName={category.name} categoryId={category.id} />
        ))}
      </div>
    </div>
  );
}
